import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { alertThresholdsAPI } from '../services/api';
import {
  AlertTriangle,
  Bell,
  Shield,
  DollarSign,
  ShoppingCart,
  Package,
  ArrowRight,
  CheckCircle2,
  X,
  Sliders,
  ExternalLink,
  Clock,
  Zap,
  Info,
  Layers,
  ChevronRight,
  Volume2,
  Key,
  CreditCard,
  HelpCircle,
  Activity,
  Server
} from 'lucide-react';

const CATEGORY_ICONS = {
  // Client scope categories
  finance: DollarSign,
  procurement: ShoppingCart,
  inventory: Package,
  security: Shield,
  // SuperAdmin scope categories
  licenses: Key,
  billing: CreditCard,
  support: HelpCircle,
  platform: Server,
  all: Layers
};

const CATEGORY_COLORS = {
  finance: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800',
  procurement: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800',
  inventory: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800',
  security: 'text-rose-600 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800',
  licenses: 'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-950/40 dark:border-purple-800',
  billing: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800',
  support: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800',
  platform: 'text-cyan-600 bg-cyan-50 border-cyan-200 dark:bg-cyan-950/40 dark:border-cyan-800'
};

export default function LoginAlertModal() {
  const { user, isSuperAdmin, isAlertsEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hasScannedThisSession, setHasScannedThisSession] = useState(false);

  // Determine portal scope dynamically based on current route and user role
  const isSuperAdminPortal = location.pathname.startsWith('/superadmin');
  const portalScope = isSuperAdminPortal ? 'superadmin' : 'client';

  // SuperAdmins always have access; Clients need alert module enabled
  const canAccess = isSuperAdmin || isAlertsEnabled;

  const requestPushPermissionAndNotify = (activeBreaches) => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        activeBreaches.slice(0, 2).forEach(b => {
          try {
            new Notification(`Global AI ERP [${b.severity.toUpperCase()}]: ${b.rule_name || b.title}`, {
              body: b.description,
              icon: '/favicon.ico',
              tag: b.id
            });
          } catch (_) {}
        });
      } else if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            activeBreaches.slice(0, 2).forEach(b => {
              try {
                new Notification(`Global AI ERP [${b.severity.toUpperCase()}]: ${b.rule_name || b.title}`, {
                  body: b.description,
                  icon: '/favicon.ico',
                  tag: b.id
                });
              } catch (_) {}
            });
          }
        });
      }
    }
  };

  const checkAlertBreaches = useCallback(async () => {
    if (!user?.id || !canAccess) return;

    // Check if dismissed recently in this session for this portal scope
    const snoozeKey = `erp_alerts_snooze_${portalScope}`;
    const snoozeTime = sessionStorage.getItem(snoozeKey);
    if (snoozeTime && Date.now() < parseInt(snoozeTime, 10)) {
      return;
    }

    try {
      setLoading(true);
      const res = await alertThresholdsAPI.getActiveBreaches({ 
        autoDispatch: false,
        scope: portalScope 
      });
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setBreaches(res.data.data);
        setIsOpen(true);
        requestPushPermissionAndNotify(res.data.data);
      }
    } catch (err) {
      console.warn(`Login alert scanner failed (${portalScope}):`, err.message);
    } finally {
      setLoading(false);
      setHasScannedThisSession(true);
    }
  }, [user?.id, canAccess, portalScope]);

  useEffect(() => {
    if (user?.id && canAccess && !hasScannedThisSession) {
      // Small timeout to allow page animation to settle before presenting executive briefing
      const timer = setTimeout(() => {
        checkAlertBreaches();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, canAccess, hasScannedThisSession, checkAlertBreaches]);

  const handleAcknowledge = () => {
    setIsOpen(false);
    sessionStorage.setItem(`erp_alerts_snooze_${portalScope}`, (Date.now() + 4 * 3600 * 1000).toString()); // 4 hours
  };

  const handleSnooze = () => {
    setIsOpen(false);
    sessionStorage.setItem(`erp_alerts_snooze_${portalScope}`, (Date.now() + 3600 * 1000).toString()); // 1 hour
  };

  const handleNavigateToBreach = (url) => {
    setIsOpen(false);
    if (url) navigate(url);
  };

  const filteredBreaches = useMemo(() => {
    if (selectedCategory === 'all') return breaches;
    return breaches.filter(b => b.category === selectedCategory);
  }, [breaches, selectedCategory]);

  const counts = useMemo(() => {
    const total = breaches.length;
    const critical = breaches.filter(b => b.severity === 'critical').length;
    const warning = breaches.filter(b => b.severity === 'warning').length;
    return { total, critical, warning };
  }, [breaches]);

  // Categories to show in tabs depending on portal scope
  const availableCategories = useMemo(() => {
    if (portalScope === 'superadmin') {
      return ['all', 'licenses', 'billing', 'support', 'security'];
    }
    return ['all', 'finance', 'procurement', 'inventory', 'security'];
  }, [portalScope]);

  if (!isOpen || breaches.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Top Glowing Header */}
        <div className={`p-6 relative overflow-hidden shrink-0 border-b ${
          portalScope === 'superadmin' 
            ? 'bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900 text-white border-purple-900/50' 
            : 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white border-indigo-900/50'
        }`}>
          <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 w-44 h-44 bg-rose-500/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute left-1/3 bottom-0 w-32 h-32 bg-amber-500/10 rounded-full blur-xl pointer-events-none" />

          <div className="flex items-start justify-between relative z-10">
            <div className="flex items-center gap-3.5">
              <div className={`w-12 h-12 rounded-xl p-0.5 shadow-lg shrink-0 ${
                portalScope === 'superadmin'
                  ? 'bg-gradient-to-tr from-purple-500 to-amber-500 shadow-purple-500/20'
                  : 'bg-gradient-to-tr from-rose-500 to-amber-500 shadow-rose-500/20'
              }`}>
                <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                  {portalScope === 'superadmin' ? (
                    <Shield className="w-6 h-6 text-purple-400 animate-pulse" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-rose-400 animate-pulse" />
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white tracking-tight">
                    {portalScope === 'superadmin' ? 'Platform Governance & Health Center' : 'Operational Alert Center'}
                  </h2>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${
                    portalScope === 'superadmin'
                      ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                      : 'bg-rose-500/20 text-rose-300 border-rose-500/30'
                  }`}>
                    {portalScope === 'superadmin' ? 'SuperAdmin Tower' : 'Action Required'}
                  </span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5">
                  {portalScope === 'superadmin'
                    ? <>Platform monitors detected <strong className="text-white">{counts.total} active governance alert{counts.total > 1 ? 's' : ''}</strong> across tenant environments on login.</>
                    : <>The Enterprise Alert & Threshold Engine detected <strong className="text-white">{counts.total} active operational breach{counts.total > 1 ? 'es' : ''}</strong> on login.</>
                  }
                </p>
              </div>
            </div>

            <button
              onClick={handleSnooze}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
              title="Close and snooze"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Severity Badges Counter */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-800 text-xs">
            {counts.critical > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-500/20 text-rose-200 border border-rose-500/30 font-semibold text-[11px]">
                <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                <span>{counts.critical} Critical Incidents</span>
              </div>
            )}
            {counts.warning > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/30 font-semibold text-[11px]">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span>{counts.warning} Warnings & Saturations</span>
              </div>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="px-6 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 flex items-center gap-1.5 overflow-x-auto shrink-0">
          {availableCategories.map((catKey) => {
            const Icon = CATEGORY_ICONS[catKey] || Layers;
            const isActive = selectedCategory === catKey;
            const countForCat = catKey === 'all' ? breaches.length : breaches.filter(b => b.category === catKey).length;
            if (countForCat === 0 && catKey !== 'all') return null;

            return (
              <button
                key={catKey}
                onClick={() => setSelectedCategory(catKey)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  isActive
                    ? portalScope === 'superadmin' ? 'bg-purple-600 text-white shadow-sm' : 'bg-indigo-600 text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="capitalize">{catKey}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}>
                  {countForCat}
                </span>
              </button>
            );
          })}
        </div>

        {/* Breaches List */}
        <div className="p-6 overflow-y-auto space-y-3.5 flex-1 divide-y divide-slate-100 dark:divide-slate-800">
          {filteredBreaches.map((breach) => {
            const isCrit = breach.severity === 'critical';
            const CatIcon = CATEGORY_ICONS[breach.category] || Layers;
            const catColor = CATEGORY_COLORS[breach.category] || CATEGORY_COLORS.finance;

            return (
              <div
                key={breach.id}
                className="pt-3.5 first:pt-0 group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 rounded-xl p-3 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl border ${catColor} shrink-0 mt-0.5`}>
                      <CatIcon className="w-4 h-4" />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wide border ${
                          isCrit
                            ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800'
                            : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800'
                        }`}>
                          {breach.severity}
                        </span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                          {breach.title}
                        </h4>
                      </div>

                      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                        {breach.description}
                      </p>

                      {/* Telemetry pill */}
                      <div className="flex items-center gap-2 mt-2 font-mono text-[11px] text-slate-500">
                        <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold">
                          Live: <strong className={isCrit ? 'text-rose-600' : 'text-amber-600'}>{breach.current_value} {breach.unit}</strong>
                        </span>
                        <span>•</span>
                        <span>Ceiling: {breach.condition_operator} {breach.threshold_value} {breach.unit}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleNavigateToBreach(breach.action_url)}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border shrink-0 transition-colors cursor-pointer ${
                      portalScope === 'superadmin'
                        ? 'bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/50 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                        : 'bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                    }`}
                  >
                    <span>{breach.action_label || 'Resolve'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <button
            onClick={() => handleNavigateToBreach(portalScope === 'superadmin' ? '/superadmin/monitoring' : '/settings/system?tab=alerts')}
            className={`text-xs font-semibold flex items-center gap-1.5 ${
              portalScope === 'superadmin'
                ? 'text-slate-500 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>{portalScope === 'superadmin' ? 'Platform Monitoring & SLA Rules' : 'Configure Alert Rules & Thresholds'}</span>
          </button>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleSnooze}
              className="flex-1 sm:flex-none px-4 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Snooze (1h)
            </button>
            
            <button
              onClick={handleAcknowledge}
              className={`flex-1 sm:flex-none px-5 py-2 text-xs font-bold rounded-xl text-white shadow-md transition-all flex items-center justify-center gap-1.5 ${
                portalScope === 'superadmin'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-purple-500/20'
                  : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-indigo-500/20'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Acknowledge & Continue</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
